import { createEffect, onCleanup, untrack } from "solid-js"
import type { PermissionRequest } from "@opencode/client/promise"
import type { Data } from "@opencode/client/solid"
import type { ServerSDK } from "@/runtime/server/client"
import { useSettings } from "@/settings/model"
import { useLanguage } from "@/runtime/i18n/language"
import { showToast } from "@/shell/notifications/toast"

const respondedLimit = 1000
const retryLimit = 2
const retryDelayMs = 1000

// Auto-approval is scoped to an exact server, directory, and session. A saved
// app-wide preference from an earlier version is never an authorization.
export function createPermissionAutoApprover(input: { sdk: ServerSDK; data: Data }) {
  const permissions = useSettings().permissions
  const language = useLanguage()
  const enabled = (directory: string, sessionID: string) =>
    permissions.autoApprove(input.sdk.scope, directory, sessionID)
  const state = { disposed: false, generation: 0, responded: new Set<string>() }

  const unsubscribe = input.sdk.event.on("permission.asked", (event) => {
    const directory = event.location?.directory
    if (directory !== undefined) approve(event.data, directory)
  })
  onCleanup(() => {
    state.disposed = true
    unsubscribe()
  })

  // Reconnects do not replay requests. Only inspect locations belonging to
  // sessions the user enabled; each returned request is checked again below.
  createEffect(() => {
    const targets = permissions.targets(input.sdk.scope)
    const generation = ++state.generation
    if (!targets.length || input.sdk.connection.status() !== "connected") return
    void untrack(() => sweepWithRetry(targets, generation, 0))
  })

  // Requests can also arrive through per-session store sync when opening an
  // idle session, without an event or a location-level response.
  createEffect(() => {
    for (const session of input.data.session.list()) {
      if (!enabled(session.location.directory, session.id)) continue
      for (const request of input.data.session.permission.list(session.id) ?? []) {
        approve(request, session.location.directory)
      }
    }
  })

  async function sweepWithRetry(
    targets: { directory: string; sessionID: string }[],
    generation: number,
    attempt: number,
  ) {
    const complete = await sweep(targets)
    if (complete || attempt >= retryLimit) return
    setTimeout(
      () => {
        if (state.disposed || generation !== state.generation) return
        void sweepWithRetry(targets, generation, attempt + 1)
      },
      retryDelayMs * (attempt + 1),
    )
  }

  async function sweep(targets: { directory: string; sessionID: string }[]) {
    // Re-read the exact sessions after reconnect so a moved session does not
    // inherit permission from its previous directory.
    const synced = await Promise.all(
      targets.map(async (target) => {
        if (!enabled(target.directory, target.sessionID)) return true
        input.data.session.invalidate(target.sessionID)
        return input.data.session.sync(target.sessionID).then(
          () => true,
          () => false,
        )
      }),
    )
    const locations = [...new Set(targets.map((target) => target.directory))]
    const listed = await Promise.all(
      locations.map((directory) =>
        input.sdk.api.permission.request
          .list({ location: { directory } })
          .then((pending) => {
            if (!state.disposed) pending.data.forEach((request) => approve(request, directory))
            return true
          })
          .catch(() => false),
      ),
    )
    return synced.every(Boolean) && listed.every(Boolean)
  }

  function approve(permission: PermissionRequest, directory: string, attempt = 0) {
    // Recheck on retries and after asynchronous sweeps: the user may switch
    // back to asking while a reply or listing is still in flight.
    const session = input.data.session.get(permission.sessionID)
    if (session && session.location.directory !== directory) return
    if (state.disposed || !enabled(directory, permission.sessionID) || state.responded.has(permission.id)) return
    remember(permission.id)
    input.sdk.api.permission
      .reply({ sessionID: permission.sessionID, requestID: permission.id, decision: "once" })
      .catch(() => {
        if (state.disposed || !enabled(directory, permission.sessionID)) {
          state.responded.delete(permission.id)
          return
        }
        if (attempt >= retryLimit) {
          permissions.setAutoApprove(input.sdk.scope, directory, permission.sessionID, false)
          state.responded.delete(permission.id)
          void input.data.session.permission.sync(permission.sessionID).catch(() => undefined)
          showToast({
            variant: "error",
            title: language.t("common.requestFailed"),
            description: language.t("toast.permissions.session.failed.description"),
          })
          return
        }
        // Keep the request reserved while waiting. A repeated event or store
        // sweep must not start a second retry chain with its own attempt count.
        setTimeout(
          () => {
            if (state.disposed || !enabled(directory, permission.sessionID)) {
              state.responded.delete(permission.id)
              return
            }
            state.responded.delete(permission.id)
            approve(permission, directory, attempt + 1)
          },
          retryDelayMs * (attempt + 1),
        )
      })
  }

  function remember(id: string) {
    state.responded.add(id)
    for (const oldest of state.responded) {
      if (state.responded.size <= respondedLimit) break
      state.responded.delete(oldest)
    }
  }
}
